/* eslint-disable max-lines */
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { isEqual } from "lodash";
import { cx } from "../../../../utils/classnames";
import Animation from "../../Animation";
import Input from "../../input/Input";
import { IList } from "../Select.types";
import Styles from "../style.module.css";
import ListItem from "./ListItem";

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const List = <T extends unknown>({
  items,
  selectedItem,
  selectedItems,
  isOpen,
  getMenuProps,
  handleScroll,
  maxHeightList = "max-h-80",
  isAbsolute = true,
  selectType,
  getInputProps,
  getOptionValue:_getOptionValue,
  enableKeyboardNav,
  inlineSearch,
  onSelectAll,
  selectAllLabel = "Select all",
  onUnselectAll,
  unselectAllLabel = "Unselect all",
  ...props
}: IList<T>) => {
  const getOptionValue = (value?: T | null) => _getOptionValue ? _getOptionValue(value) : value;
  // Nothing left to add once every listed option is already picked, so the
  // button flips to unselect — or goes flat if the consumer didn't supply one.
  const allListedSelected =
    items.length > 0 &&
    items.every((item) =>
      selectedItems?.some((el) =>
        isEqual(getOptionValue(el), getOptionValue(item))
      )
    );
  const unselectMode = allListedSelected && !!onUnselectAll;
  return (
    <Animation
      type="fade-top"
      show={isOpen}
      duration={300}
      leaveInDom
    >
      <div
        className={cx([
          isAbsolute && "absolute z-50",
          isOpen && "mt-2 py-1",
          "w-full bg-white rounded-lg ring-1 ring-blue-stroke "
        ])}
      >
        <ul
          className={cx([
            "overflow-y-scroll",
            Styles.scrollThin,
            isOpen ? maxHeightList : "h-0 max-h-0"
          ])}
          {...getMenuProps(
            { onScroll: handleScroll },
            { suppressRefError: true }
          )}
        >
          {/* Pinned so it stays reachable while scrolling a long result set.
              It lives INSIDE the <ul> on purpose: that element carries
              getMenuProps, and Downshift closes on any mouseup outside its
              input/menu/toggle refs — a header above the list would dismiss the
              dropdown on click. */}
          {onSelectAll && items.length > 0 ? (
            <li className="sticky top-0 z-10 bg-white px-3 py-2 flex justify-end
             border-b border-blue-stroke">
              <button
                type="button"
                disabled={allListedSelected && !unselectMode}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  unselectMode ? onUnselectAll?.(items) : onSelectAll(items)
                }
                className={cx([
                  "text-sm font-medium",
                  unselectMode
                    ? "text-red-600 hover:underline"
                    : allListedSelected
                      ? "text-neutral cursor-not-allowed"
                      : "text-primary hover:underline"
                ])}
              >
                {unselectMode ? unselectAllLabel : selectAllLabel} ({items.length})
              </button>
            </li>
          ) : null}
          {/* inlineSearch: the trigger input already is the search box, so a
              second one in here would be a duplicate. */}
          {selectType === "multiple" && !inlineSearch ? (
            <li className="px-3 py-2">
              {enableKeyboardNav ? (
                // Opt-in: this input IS the Downshift combobox input. Spread the
                // full getInputProps() (ref, onKeyDown, onChange, value,
                // aria-activedescendant, role=combobox, ...) so ↑/↓/Enter/Esc and
                // roving highlight land here. Display-only props go first so they
                // never clobber Downshift's handlers/ref.
                // suppressRefError: the menu (and this input) is unmounted while
                // closed, so Downshift's input ref is briefly empty then — the
                // flag only silences that dev warning; the ref still binds every
                // time the input mounts, so keyboard nav is unaffected.
                <Input
                  paddingType="fill"
                  placeholder="Search"
                  inputClassName="rounded-[4px]"
                  before={<MagnifyingGlassIcon className="w-4 h-4" />}
                  {...getInputProps({}, { suppressRefError: true })}
                />
              ) : (
                <Input
                  onChange={getInputProps().onChange}
                  paddingType="fill"
                  placeholder="Search"
                  inputClassName="rounded-[4px]"
                  before={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              )}
            </li>
          ) : null}
          {items.length === 0 ? (
            <li className="p-3 text-sm text-neutral">No results</li>
          ) : (
            items.map((item, index) => (
              <ListItem
                key={`${index}`}
                active={
                  !!(
                    isEqual(getOptionValue(item), getOptionValue(selectedItem as T)) ||
                    selectedItems?.some((el) => isEqual(getOptionValue(el), getOptionValue(item)))
                  )
                }
                index={index}
                item={item}
                {...props}
              />
            ))
          )}
        </ul>
      </div>
    </Animation>
  );
};

export default List;
